class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('click', this.onDrawerAction.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  onDrawerAction(event) {
    const checkoutButton = event.target.closest('[data-cart-checkout]');
    if (checkoutButton) {
      event.preventDefault();
      this.submitCheckout(checkoutButton);
      return;
    }

    const addButton = event.target.closest('[data-cart-add-variant]');
    if (addButton) {
      event.preventDefault();
      this.addVariant(addButton.dataset.cartAddVariant, addButton);
      return;
    }

    const addOnToggle = event.target.closest('[data-cart-addon-variant]');
    if (addOnToggle) {
      event.preventDefault();
      const isActive = addOnToggle.dataset.cartAddonActive === 'true';
      if (isActive) {
        this.changeVariant(addOnToggle.dataset.cartAddonVariant, 0, addOnToggle);
      } else {
        this.addVariant(addOnToggle.dataset.cartAddonVariant, addOnToggle);
      }
    }
  }

  submitCheckout(trigger) {
    if (trigger.disabled) return;

    const form = document.getElementById('CartDrawer-Form');
    if (form) {
      let checkoutInput = form.querySelector('input[name="checkout"]');
      if (!checkoutInput) {
        checkoutInput = document.createElement('input');
        checkoutInput.type = 'hidden';
        checkoutInput.name = 'checkout';
        checkoutInput.value = 'checkout';
        form.appendChild(checkoutInput);
      }
      form.submit();
      return;
    }

    const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    window.location.href = `${root}checkout`;
  }

  addVariant(variantId, trigger) {
    if (!variantId) return;
    this.setActionLoading(trigger, true);

    const body = JSON.stringify({
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body })
      .then((response) => response.json())
      .then((parsedState) => this.renderContents(parsedState))
      .catch(() => this.showActionError())
      .finally(() => this.setActionLoading(trigger, false));
  }

  changeVariant(variantId, quantity, trigger) {
    if (!variantId) return;
    this.setActionLoading(trigger, true);

    const body = JSON.stringify({
      id: String(variantId),
      quantity,
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
      .then((response) => response.json())
      .then((parsedState) => this.renderContents(parsedState))
      .catch(() => this.showActionError())
      .finally(() => this.setActionLoading(trigger, false));
  }

  setActionLoading(trigger, isLoading) {
    if (!trigger) return;
    trigger.toggleAttribute('disabled', isLoading);
    trigger.classList.toggle('is-loading', isLoading);
  }

  showActionError() {
    const errors = document.getElementById('CartDrawer-CartErrors');
    if (errors && window.cartStrings) errors.textContent = window.cartStrings.error;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
